import React, { useState } from 'react';
import { Document, Packer, Paragraph, HeadingLevel, TextRun, Table, TableRow, TableCell, WidthType, AlignmentType, Header, Footer } from 'docx';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { motion } from 'framer-motion';
import { getLocalizedFeatureCatalog, getLocalizedPricingPlans, useLanguage } from '../i18n';
import {
    FaCheck, FaArrowRight, FaStar, FaPhoneAlt, FaEnvelope,
    FaRocket, FaCubes, FaShoppingCart, FaPencilRuler, FaPuzzlePiece,
    FaUserTie, FaInfoCircle, FaGift, FaLanguage, FaChevronDown, FaTimes,
    FaFilePdf, FaFileWord
} from 'react-icons/fa';

const PricingPackages = () => {
    const { language, t } = useLanguage();
    const pricingPlans = getLocalizedPricingPlans(language);
    const featureCatalog = getLocalizedFeatureCatalog(language);
    const [showCompare, setShowCompare] = useState(false);

    const generateDocForPlan = async (plan) => {
        // Gather cumulative features for clarity in exported document
        const planIndex = pricingPlans.findIndex(p => p.id === plan.id);
        const cumulative = new Set();
        pricingPlans.slice(0, planIndex + 1).forEach(pl => pl.featureAdditions?.forEach(f => cumulative.add(f)));
        const cumulativeFeatures = featureCatalog.filter(fc => cumulative.has(fc.key));
        const newFeatures = featureCatalog.filter(fc => plan.featureAdditions?.includes(fc.key));

        const addHeading = (text: string, level: any = HeadingLevel.HEADING_2) => new Paragraph({ text, heading: level, spacing: { after: 120 } } as any);
        const addPara = (text) => new Paragraph({ children: [new TextRun({ text, font: 'Arial', size: 22 })], spacing: { after: 80 } });
        const addBullet = (text) => new Paragraph({ text, bullet: { level: 0 }, spacing: { after: 40 } });

        const maintenanceRows = () => {
            const d = plan.maintenance?.domain; const h = plan.maintenance?.hosting; const s = plan.maintenance?.support;
            const rows = [];
            if (d) rows.push(['Domain', d.includedYears > 0 ? `Miễn phí ${d.includedYears} năm đầu${d.annualFee ? `, sau đó ${d.annualFee.toLocaleString()}₫/năm` : ''}` : (d.note || (d.annualFee ? d.annualFee.toLocaleString() + '₫/năm' : 'Theo yêu cầu'))]);
            if (h) rows.push(['Hosting', h.includedYears > 0 ? `Miễn phí ${h.includedYears} năm${h.annualFee ? `, sau đó ${h.annualFee.toLocaleString()}₫/năm` : ''}` : (h.note || (h.annualFee ? h.annualFee.toLocaleString() + '₫/năm' : 'Theo yêu cầu'))]);
            if (s) rows.push(['Support', `Bảo hành ${s.includedMonths} tháng${s.afterAnnualFee ? `, sau đó ${s.afterAnnualFee.toLocaleString()}₫/năm` : ''}${s.scope ? `\nScope: ${s.scope}` : ''}`]);
            return rows.map(r => new TableRow({
                children: [
                    new TableCell({ width: { size: 25, type: WidthType.PERCENTAGE }, children: [new Paragraph({ text: r[0], bold: true } as any)] }),
                    new TableCell({ width: { size: 75, type: WidthType.PERCENTAGE }, children: [new Paragraph(r[1])] }),
                ]
            }));
        };

        const benefits = plan.benefits || {};
        const benefitList = [
            `Banner tặng: ${benefits.banners ?? '-'}`,
            `Nhập liệu: ${benefits.dataEntryItems ?? '-'} bài / SP`,
            `SSL: ${benefits.ssl ? 'Có' : 'Không'}`,
            `Domain miễn phí: ${benefits.freeDomain ? 'Có' : 'Không'}`,
            `Mã nguồn: ${benefits.sourceCode ? 'Bàn giao' : 'Không'}`,
            `Bảo hành: ${benefits.warrantyMonths ?? '-'} tháng`
        ];

        const logoParagraph = new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: 'DevenflyWeb', bold: true, size: 32 })] });
        const footerParagraph = new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: '© ' + new Date().getFullYear() + ' DevenflyWeb – Liên hệ: hongducmmo@gmail.com | 0918.873.984', size: 18 })] });

        const doc = new Document({
            sections: [
                {
                    headers: { default: new Header({ children: [logoParagraph] }) },
                    footers: { default: new Footer({ children: [footerParagraph] }) },
                    properties: {},
                    children: [
                        new Paragraph({ text: 'BẢNG GIÁ WEBSITE WORDPRESS', heading: HeadingLevel.TITLE, spacing: { after: 200 }, alignment: AlignmentType.CENTER }),
                        addHeading(`${plan.name} – ${plan.price ? plan.price.toLocaleString() + '₫ (1 lần)' : 'Liên hệ'}`, HeadingLevel.HEADING_1),
                        addPara(plan.description || ''),
                        addHeading('Thông tin chung'),
                        addBullet(`Phù hợp: ${plan.audience}`),
                        addBullet(`Template / Thiết kế: ${plan.template}`),
                        addBullet(`Ngôn ngữ: ${plan.languages}`),
                        addBullet(`Thời gian triển khai: ${plan.timeline}`),
                        addHeading('Chức năng & tích hợp'),
                        ...(plan.integrations || []).map(i => addBullet(i)),
                        addHeading('Tính năng (cộng dồn)'),
                        ...cumulativeFeatures.map(f => addBullet(f.label)),
                        addHeading('Tính năng mới ở gói này'),
                        ...newFeatures.map(f => addBullet(f.label)),
                        addHeading('Quyền lợi'),
                        ...benefitList.map(b => addBullet(b)),
                        addHeading('Khuyến mãi & bảo hành'),
                        ...(plan.promotions || []).map(promo => addBullet(promo)),
                        addHeading('Chi phí duy trì'),
                        new Table({
                            width: { size: 100, type: WidthType.PERCENTAGE },
                            rows: [
                                new TableRow({
                                    children: [
                                        new TableCell({ children: [new Paragraph({ text: 'Hạng mục', bold: true } as any)] }),
                                        new TableCell({ children: [new Paragraph({ text: 'Chi tiết', bold: true } as any)] })
                                    ]
                                }),
                                ...maintenanceRows()
                            ]
                        }),
                        addPara('Tài liệu tạo tự động – vui lòng liên hệ để nhận tư vấn chi tiết hơn.')
                    ]
                }
            ]
        });

        const blob = await Packer.toBlob(doc);
        saveAs(blob, `Bang_gia_${plan.name}.docx`);
    };

    const exportPdfForPlan = async (plan) => {
        // Create an off-screen container replicating content for PDF
        const container = document.createElement('div');
        container.style.padding = '24px';
        container.style.width = '800px';
        container.style.fontFamily = 'Arial, sans-serif';
        const format = (n) => n != null ? n.toLocaleString() + '₫' : '—';
        const cumulativeKeys = [...new Set(pricingPlans.slice(0, pricingPlans.findIndex(p => p.id === plan.id) + 1).flatMap(pl => pl.featureAdditions || []))];
        const cumulativeList = cumulativeKeys.map(k => { const f = featureCatalog.find(fc => fc.key === k); return `<li>${f ? f.label : k}</li>` }).join('');
        const newList = (plan.featureAdditions || []).map(k => { const f = featureCatalog.find(fc => fc.key === k); return `<li>${f ? f.label : k}</li>` }).join('');
        // Maintenance references
        const d = plan.maintenance?.domain; const h = plan.maintenance?.hosting; const s = plan.maintenance?.support;
        // Helper to compute component cost for a given year
        const domainCostForYear = (year) => {
            if (!d) return 0; if (d.includedYears && d.includedYears >= year) return 0; // still free
            return d.annualFee ?? null; // if fee undefined => Theo yêu cầu
        };
        const hostingCostForYear = (year) => {
            if (!h) return 0; if (h.includedYears && h.includedYears >= year) return 0; return h.annualFee ?? null;
        };
        const supportCostForYear = (year) => {
            if (!s || !s.afterAnnualFee) return 0; // no paid support defined
            const startPaidMonth = (s.includedMonths || 0) + 1; // month support becomes paid
            const yearStartMonth = (year - 1) * 12 + 1;
            return yearStartMonth >= startPaidMonth ? s.afterAnnualFee : 0;
        };
        const sumCosts = (parts) => parts.includes(null) ? null : parts.reduce((a, b) => a + b, 0);
        const year1Parts = [domainCostForYear(1), hostingCostForYear(1), supportCostForYear(1)];
        const year2Parts = [domainCostForYear(2), hostingCostForYear(2), supportCostForYear(2)];
        const year1Maintenance = sumCosts(year1Parts);
        const year2Maintenance = sumCosts(year2Parts);
        // Annual cost when warranty ends (after includedMonths)
        const warrantyMonths = s?.includedMonths || 0; const warrantyYears = Math.ceil(warrantyMonths / 12) || 0;
        const postWarrantyParts = [domainCostForYear(warrantyYears + 1), hostingCostForYear(warrantyYears + 1), supportCostForYear(warrantyYears + 1)];
        const postWarrantyAnnual = sumCosts(postWarrantyParts);
        const costDisplay = (v) => v === null ? 'Theo yêu cầu' : (v > 0 ? v.toLocaleString() + '₫' : '0₫');
        const devCost = plan.price ? plan.price.toLocaleString() + '₫' : 'Liên hệ';
        // Annual total after all free periods originally (legacy summary)
        const annualDomain = d?.annualFee || 0; const annualHosting = h?.annualFee || 0; const annualSupport = s?.afterAnnualFee || 0; const annualTotal = [annualDomain, annualHosting, annualSupport].filter(v => v > 0).reduce((a, b) => a + b, 0);
        container.innerHTML = `
                        <style>
                                :root {
                                    --color-primary: #166ffe; /* primary-600 */
                                    --color-primary-dark: #082e70; /* primary-900 */
                                    --color-primary-mid: #0b47a5; /* primary-800 */
                                    --color-primary-accent: #3b82f6; /* primary-500 */
                                    --color-bg-soft: #f1f5f9; /* secondary-100 */
                                    --color-bg-soft-alt: #eff6ff; /* primary-50 */
                                    --color-border: #cbd5e1; /* secondary-300 */
                                    --color-text: #334155; /* secondary-700 */
                                    --color-text-dark: #0f172a; /* secondary-900 */
                                    --color-gradient-start: #eff6ff;
                                    --color-gradient-end: #ffffff;
                                }
                                * { box-sizing: border-box; }
                body { margin:0; }
                h1,h2,h3 { font-family: Arial, sans-serif; }
                                .header-bar { position:relative; text-align:center; padding:10px 0 14px; border-bottom:2px solid var(--color-primary-dark); margin-bottom:14px; }
                                .brand { font-size:26px; font-weight:700; letter-spacing:1px; color:var(--color-primary-dark); }
                                .subtitle { font-size:16px; font-weight:600; color:var(--color-primary); margin-top:4px; }
                                .watermark { position:fixed; top:40%; left:50%; transform:translate(-50%,-50%) rotate(-25deg); font-size:90px; font-weight:700; color:var(--color-primary-dark); opacity:0.06; pointer-events:none; z-index:0; white-space:nowrap; }
                .content { position:relative; z-index:1; }
                                .section { margin:18px 0 10px; font-size:14px; font-weight:700; text-transform:uppercase; letter-spacing:.6px; color:var(--color-primary-dark); border-left:5px solid var(--color-primary); padding:4px 0 4px 10px; break-after:avoid; background:linear-gradient(90deg,var(--color-bg-soft-alt),transparent); }
                .two-col { column-count:2; column-gap:34px; }
                                ul.feature-list { margin:4px 0 10px 18px; padding:0; font-size:13px; line-height:1.5; color:var(--color-text); }
                ul.feature-list li { break-inside:avoid; }
                                .info-grid { display:grid; grid-template-columns:1fr 1fr; gap:6px 18px; font-size:13px; color:var(--color-text); }
                .kv { display:flex; align-items:flex-start; }
                                .kv span.key { width:110px; font-weight:600; color:var(--color-text); }
                                table.pricing, table.cost-estimate { width:100%; border-collapse:collapse; font-size:12px; margin:6px 0 14px; }
                                table.pricing th, table.cost-estimate th { background:var(--color-primary-dark); color:#fff; font-weight:600; text-align:left; padding:6px 8px; font-size:12px; }
                                table.pricing td, table.cost-estimate td { border:1px solid var(--color-border); padding:6px 8px; vertical-align:top; color:var(--color-text); }
                                .summary-box { border:1px solid var(--color-primary); background:linear-gradient(135deg,var(--color-gradient-start),var(--color-gradient-end)); padding:12px 16px; border-radius:10px; font-size:12px; margin:12px 0 8px; }
                                .summary-box h4 { margin:0 0 8px; font-size:14px; color:var(--color-primary-dark); }
                                .totals { display:flex; gap:18px; margin-top:6px; }
                                .totals .block { flex:1; background:var(--color-bg-soft); border:1px solid var(--color-border); padding:10px 14px; border-radius:8px; }
                                .totals .block h5 { margin:0 0 4px; font-size:12px; font-weight:600; color:var(--color-text); }
                                .totals .block .value { font-size:16px; font-weight:700; color:var(--color-text-dark); }
                                .footer { font-size:10px; text-align:center; margin-top:20px; opacity:0.65; border-top:1px solid var(--color-border); padding-top:6px; color:var(--color-text); }
            </style>
            <div class="watermark">DevenflyWeb</div>
            <div class="header-bar">
                <div class="brand">DevenflyWeb</div>
                <div class="subtitle">BẢNG GIÁ WEBSITE WORDPRESS · ${plan.name} (${plan.price ? plan.price.toLocaleString() + '₫' : 'Liên hệ'})</div>
            </div>
            <div class="content two-col">
                <div style="font-size:13px; line-height:1.55; margin-bottom:4px; break-after:avoid;">${plan.description || ''}</div>
                <div class="section">Thông tin chung</div>
                <div class="info-grid">
                    <div class="kv"><span class="key">Phù hợp:</span><span>${plan.audience}</span></div>
                    <div class="kv"><span class="key">Thiết kế:</span><span>${plan.template}</span></div>
                    <div class="kv"><span class="key">Ngôn ngữ:</span><span>${plan.languages}</span></div>
                    <div class="kv"><span class="key">Thời gian:</span><span>${plan.timeline}</span></div>
                </div>
                <div class="section">Chức năng & tích hợp</div>
                <ul class="feature-list">${(plan.integrations || []).map(i => `<li>${i}</li>`).join('')}</ul>
                <div class="section">Tính năng (cộng dồn)</div>
                <ul class="feature-list">${cumulativeList}</ul>
                <div class="section">Tính năng mới ở gói này</div>
                <ul class="feature-list">${newList}</ul>
                <div class="section">Quyền lợi</div>
                <ul class="feature-list">
                    <li>Banner tặng: ${plan.benefits?.banners ?? '-'}</li>
                    <li>Nhập liệu: ${plan.benefits?.dataEntryItems ?? '-'} bài / SP</li>
                    <li>SSL: ${plan.benefits?.ssl ? 'Có' : 'Không'}</li>
                    <li>Domain miễn phí: ${plan.benefits?.freeDomain ? 'Có' : 'Không'}</li>
                    <li>Mã nguồn: ${plan.benefits?.sourceCode ? 'Bàn giao' : 'Không'}</li>
                    <li>Bảo hành: ${plan.benefits?.warrantyMonths ?? '-'} tháng</li>
                </ul>
                <div class="section">Khuyến mãi & bảo hành</div>
                <ul class="feature-list">${(plan.promotions || []).map(p => `<li>${p}</li>`).join('')}</ul>
                <div class="section">Chi phí duy trì (sau miễn phí)</div>
                <table class="pricing">
                    <thead><tr><th>Hạng mục</th><th>Chi tiết</th></tr></thead>
                    <tbody>
                        ${(function () { const rows = []; if (d) rows.push(['Domain', d.includedYears > 0 ? `Miễn phí ${d.includedYears} năm đầu${d.annualFee ? `, sau đó ${d.annualFee.toLocaleString()}₫/năm` : ''}` : (d.note || (d.annualFee ? d.annualFee.toLocaleString() + '₫/năm' : 'Theo yêu cầu'))]); if (h) rows.push(['Hosting', h.includedYears > 0 ? `Miễn phí ${h.includedYears} năm${h.annualFee ? `, sau đó ${h.annualFee.toLocaleString()}₫/năm` : ''}` : (h.note || (h.annualFee ? h.annualFee.toLocaleString() + '₫/năm' : 'Theo yêu cầu'))]); if (s) rows.push(['Support', `Bảo hành ${s.includedMonths} tháng${s.afterAnnualFee ? `, sau đó ${s.afterAnnualFee.toLocaleString()}₫/năm` : ''}<br/>Scope: ${s.scope || ''}`]); return rows.map(r => `<tr><td>${r[0]}</td><td style='white-space:pre-line;'>${r[1]}</td></tr>`).join('') })()}
                    </tbody>
                </table>
                <div class="summary-box" style="break-inside:avoid;">
                    <h4>Tổng quan chi phí</h4>
                    <div class="totals">
                        <div class="block"><h5>Phát triển</h5><div class="value">${devCost}</div></div>
                        <div class="block"><h5>Duy trì / năm</h5><div class="value">${annualTotal > 0 ? annualTotal.toLocaleString() + '₫/năm' : 'Theo yêu cầu'}</div></div>
                    </div>
                </div>
                <div class="section">Bảng dự toán chi phí</div>
                <table class="cost-estimate" style="break-inside:avoid;">
                    <thead><tr><th>Mục</th><th>Chi phí</th></tr></thead>
                    <tbody>
                        <tr><td>Phí phát triển (1 lần)</td><td>${devCost}</td></tr>
                        <tr><td>Phí duy trì năm 1</td><td>${costDisplay(year1Maintenance)}</td></tr>
                        <tr><td>Phí duy trì năm 2</td><td>${costDisplay(year2Maintenance)}</td></tr>
                        <tr><td>Phí duy trì sau hết bảo hành</td><td>${costDisplay(postWarrantyAnnual)}</td></tr>
                    </tbody>
                </table>
            </div>
            <div class="footer">© ${new Date().getFullYear()} DevenflyWeb – hongducmmo@gmail.com | 0918.873.984</div>
        `;
        document.body.appendChild(container);
        const canvas = await html2canvas(container, { scale: 2 });
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'pt', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 20;
        const imgWidth = pageWidth - margin * 2;
        const imgHeight = canvas.height * imgWidth / canvas.width;
        let heightLeft = imgHeight;
        let position = margin;

        // Add first page
        pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
        heightLeft -= (pageHeight - margin * 2);

        // Subsequent pages (reuse same image with upward offset)
        while (heightLeft > 0) {
            pdf.addPage();
            position = margin - (imgHeight - heightLeft);
            pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
            heightLeft -= (pageHeight - margin * 2);
        }
        pdf.save(`Bang_gia_${plan.name}.pdf`);
        document.body.removeChild(container);
    };

    return (
        <section id="pricing" className="py-24 bg-white">
            <div className="container mx-auto px-4">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="text-center max-w-4xl mx-auto mb-16 relative"
                >
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5">{t('pricingTitle')} <span className="text-primary-600">{t('pricingTitleAccent')}</span></h2>
                    <p className="text-lg text-gray-600">{t('pricingSubtitle')}</p>
                    <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
                        <button
                            type="button"
                            onClick={() => setShowCompare(true)}
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold shadow-sm transition-colors"
                        >
                            {t('compare')} <FaArrowRight className="text-xs" />
                        </button>
                    </div>
                </motion.div>

                {(() => {
                    const firstRow = pricingPlans.slice(0, 3);
                    const secondRow = pricingPlans.slice(3);

                    const planIconMap = {
                        Standard: <FaCubes className="text-primary-600" />,
                        Professional: <FaUserTie className="text-primary-600" />,
                        Advanced: <FaShoppingCart className="text-primary-600" />,
                        Customizer: <FaPencilRuler className="text-primary-600" />,
                        Extra: <FaPuzzlePiece className="text-primary-600" />
                    };

                    const compactCopy = (value: string) => ({
                        'Sử dụng mẫu giao diện dựng sẵn': 'Mẫu dựng sẵn',
                        'Mẫu giao diện dựng sẵn (có chỉnh sửa)': 'Mẫu + chỉnh sửa',
                        'Chọn mẫu giao diện (có chỉnh sửa)': 'Mẫu + chỉnh sửa',
                        'Thiết kế giao diện độc quyền': 'Thiết kế độc quyền',
                        'Thiết kế độc quyền theo yêu cầu': 'Thiết kế theo yêu cầu',
                        '1 ngôn ngữ (Tiếng Việt)': 'Tiếng Việt',
                        '2 ngôn ngữ (Việt & Anh)': 'Việt + Anh',
                        'Tương thích mọi trình duyệt & thiết bị': 'Responsive',
                        'Tối ưu hóa SEO': 'SEO cơ bản'
                    }[value] || value);

                    const LegacyCard = (plan) => (
                        <motion.div
                            key={plan.id}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className={`relative flex flex-col h-full rounded-2xl border ${plan.popular ? 'border-primary-600 shadow-xl' : 'border-gray-200 shadow-md'} bg-white overflow-hidden group`}
                        >
                            {plan.popular && (
                                <div className="absolute top-0 left-0 right-0 text-center">
                                    <span className="inline-flex items-center gap-1 bg-primary-600 text-white text-xs font-semibold px-3 py-1 rounded-b-lg">
                                        <FaStar className="text-yellow-300" /> {t('popular')}
                                    </span>
                                </div>
                            )}
                            <div className="p-6 flex flex-col flex-1">
                                <div className="mb-4">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center">
                                            {planIconMap[plan.name] || <FaRocket className="text-primary-600" />}
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                                    </div>
                                    <p className="text-sm text-gray-600 leading-relaxed min-h-[40px]">{plan.description}</p>
                                </div>
                                <div className="mb-4">
                                    {plan.price !== null ? (
                                        <div className="flex items-end gap-1">
                                            <span className="text-2xl font-extrabold text-primary-600">{plan.price.toLocaleString()}₫</span>
                                            <span className="text-xs text-gray-500 mb-1">(1 lần)</span>
                                        </div>
                                    ) : (
                                        <div className="text-xl font-bold text-primary-600">{t('contact')}</div>
                                    )}
                                </div>
                                <div className="space-y-5 text-sm flex-1">
                                    {plan.audience && (
                                        <div>
                                            <div className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
                                                <FaUserTie className="text-primary-600" /> {t('suitableFor')}
                                            </div>
                                            <p className="text-gray-600 leading-snug">{plan.audience}</p>
                                        </div>
                                    )}
                                    <div>
                                        <div className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
                                            <FaInfoCircle className="text-primary-600" /> {t('websiteInfo')}
                                        </div>
                                        <ul className="space-y-1 text-gray-600">
                                            {plan.template && <li>{plan.template}</li>}
                                            {plan.languages && <li className="flex items-center gap-2"><FaLanguage className="text-primary-500" /> {plan.languages}</li>}
                                            {plan.integrations?.length > 0 && (
                                                <li className="mt-1">
                                                    <span className="block font-medium text-gray-700">{t('functionality')}</span>
                                                    <ul className="mt-1 space-y-1 pl-3 list-disc marker:text-primary-600">
                                                        {plan.integrations.map((it, idx) => <li key={idx}>{it}</li>)}
                                                    </ul>
                                                </li>
                                            )}
                                            {plan.timeline && <li className="mt-1">{t('timeline')} {plan.timeline}</li>}
                                        </ul>
                                    </div>
                                    {(() => {
                                        // Show only new (delta) features introduced in THIS plan (no repeats of previous plans)
                                        const planIndex = pricingPlans.findIndex(p => p.id === plan.id);
                                        const prevName = planIndex > 0 ? pricingPlans[planIndex - 1].name : null;
                                        const newKeys = plan.featureAdditions || [];
                                        const labels = featureCatalog.filter(fc => newKeys.includes(fc.key));
                                        if (labels.length === 0) return null;
                                        const heading = planIndex === 0 ? 'Tính năng' : `Tất cả tính năng gói ${prevName} +`;
                                        return (
                                            <div>
                                                <div className="font-semibold text-gray-800 mt-4 mb-1 flex items-center gap-2">
                                                    <FaInfoCircle className="text-primary-600" /> {heading}
                                                </div>
                                                <ul className="space-y-1 text-gray-600 text-[13px] leading-snug">
                                                    {labels.slice(0, 4).map(l => (
                                                        <li key={l.key} className="flex gap-2">
                                                            <FaCheck className="mt-0.5 text-primary-500 flex-shrink-0" />
                                                            <span>{l.label}</span>
                                                        </li>
                                                    ))}
                                                    {labels.length > 4 && (
                                                        <li className="text-xs text-primary-600">+ {labels.length - 4} thêm...</li>
                                                    )}
                                                </ul>
                                            </div>
                                        );
                                    })()}
                                    {plan.promotions?.length > 0 && (
                                        <div>
                                            <div className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
                                                    <FaGift className="text-primary-600" /> {t('promotions')}
                                            </div>
                                            <ul className="space-y-1">
                                                {plan.promotions.map((p, idx) => (
                                                    <li key={idx} className="flex items-start gap-2 text-gray-600">
                                                        <FaCheck className="mt-0.5 text-primary-600 flex-shrink-0" />
                                                        <span>{p}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                                {plan.maintenance && (() => {
                                    const [openMap] = useState({}); // placeholder to allow stateful refactor later if needed
                                    const d = plan.maintenance.domain;
                                    const h = plan.maintenance.hosting;
                                    const s = plan.maintenance.support;
                                    // Annual recurring cost after free periods (when they start applying)
                                    const annualDomain = d.annualFee || 0;
                                    const annualHosting = h.annualFee || 0;
                                    const annualSupport = s.afterAnnualFee || 0;
                                    const totalAnnual = [annualDomain, annualHosting, annualSupport].filter(v => v > 0).reduce((a, b) => a + b, 0);
                                    return (
                                        <div className="mt-6 border border-primary-200 rounded-lg p-4 bg-gradient-to-br from-primary-50 to-white">
                                            <div className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                                <FaInfoCircle className="text-primary-600" /> {t('maintenance')}
                                            </div>
                                            <ul className="text-xs text-gray-600 space-y-1 mb-3">
                                                <li>Domain: {d.includedYears > 0 ? `Miễn phí ${d.includedYears} năm đầu${d.startFromYear ? `, sau đó ${annualDomain.toLocaleString()}₫/năm` : ''}` : (d.note || `${annualDomain ? annualDomain.toLocaleString() + "₫/năm" : "Theo yêu cầu"}`)}</li>
                                                <li>Hosting: {h.includedYears > 0 ? `Miễn phí ${h.includedYears} năm${h.startFromYear ? `, sau đó ${annualHosting.toLocaleString()}₫/năm` : ''}` : (h.note || `${annualHosting ? annualHosting.toLocaleString() + "₫/năm" : "Theo yêu cầu"}`)}</li>
                                                <li className="group">
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            const details = e.currentTarget.closest('li')?.querySelector('.support-scope');
                                                            const icon = e.currentTarget.querySelector<HTMLElement>('.chevron');
                                                            if (details) {
                                                                const hidden = details.classList.contains('hidden');
                                                                details.classList.toggle('hidden');
                                                                if (icon) { icon.style.transform = hidden ? 'rotate(180deg)' : 'rotate(0deg)'; }
                                                            }
                                                        }}
                                                        className="w-full flex items-center justify-between gap-2 text-left text-gray-700 hover:text-primary-600 transition-colors"
                                                    >
                                                        <span className="text-[11px] font-medium">
                                                            Support: Bảo hành {s.includedMonths} tháng{s.afterAnnualFee ? `, sau đó ${annualSupport.toLocaleString()}₫/năm` : ''}
                                                        </span>
                                                        <FaChevronDown className="chevron text-primary-600 text-xs transition-transform duration-200" />
                                                    </button>
                                                    <div className="support-scope hidden mt-2 text-[11px] leading-relaxed bg-white/70 border border-primary-100 rounded p-2">
                                                        <ul className="list-disc pl-4 space-y-1">
                                                            {s.scope.split(',').map((part, idx) => (
                                                                <li key={idx}>{part.trim()}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </li>
                                            </ul>
                                            <div className="text-xs font-medium text-gray-700 flex items-center justify-between">
                                                <span>{t('annualTotal')}</span>
                                                <span className="text-primary-600 font-semibold">{totalAnnual > 0 ? totalAnnual.toLocaleString() + "₫/năm" : 'Theo yêu cầu'}</span>
                                            </div>
                                        </div>
                                    );
                                })()}
                                <motion.button
                                    onClick={() => {
                                        const el = document.getElementById('contact');
                                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                                    }}
                                    whileHover={{ scale: 1.03 }}
                                    whileTap={{ scale: 0.97 }}
                                    className={`mt-6 w-full inline-flex items-center justify-center gap-2 font-semibold text-sm px-5 py-3 rounded-lg transition-colors ${plan.popular ? 'bg-primary-600 text-white hover:bg-primary-700' : 'bg-gray-900 text-white hover:bg-primary-600'}`}
                                >
                                    {plan.buttonText || 'Liên hệ'} <FaArrowRight className="text-xs" />
                                </motion.button>
                                <div className="mt-3 flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => generateDocForPlan(plan)}
                                        className="flex-1 inline-flex items-center justify-center gap-2 font-medium text-[13px] px-4 py-2 rounded-lg border border-primary-200 text-primary-700 hover:bg-primary-50 transition-colors"
                                    >
                                        <FaFileWord className="text-primary-600" />
                                        <span>{t('downloadDoc')}</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => exportPdfForPlan(plan)}
                                        className="flex-1 inline-flex items-center justify-center gap-2 font-medium text-[13px] px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                        <FaFilePdf className="text-red-600" />
                                        <span>{t('downloadPdf')}</span>
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    );

                    const Card = (plan) => {
                        const highlights = (plan.integrations || []).slice(0, 2).map(compactCopy);

                        return (
                            <motion.div
                                key={plan.id}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.45 }}
                                className={`relative flex h-full flex-col border ${plan.popular ? 'border-primary-600' : 'border-gray-200'} bg-white`}
                            >
                                {plan.popular && (
                                    <div className="border-b border-primary-200 bg-primary-50 px-6 py-2 text-xs font-bold uppercase tracking-[0.14em] text-primary-700">
                                        <FaStar className="mr-1 inline text-primary-600" /> Được chọn nhiều
                                    </div>
                                )}
                                <div className="flex flex-1 flex-col p-6">
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50">
                                            {planIconMap[plan.name] || <FaRocket className="text-primary-600" />}
                                        </div>
                                        <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>
                                    </div>

                                    <div className="mt-5 flex items-end gap-1">
                                        {plan.price !== null ? (
                                            <>
                                                <span className="text-2xl font-extrabold text-primary-600">{plan.price.toLocaleString()}₫</span>
                                                <span className="mb-1 text-sm text-gray-500">một lần</span>
                                            </>
                                        ) : (
                                            <span className="text-2xl font-bold text-primary-600">Liên hệ</span>
                                        )}
                                    </div>

                                    <div className="mt-5 grid grid-cols-3 gap-3 border-y border-gray-200 py-4 text-sm">
                                        <div>
                                            <p className="font-medium text-gray-500">Giao diện</p>
                                            <p className="mt-1 font-semibold text-gray-800">{compactCopy(plan.template || 'Theo yêu cầu')}</p>
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-500">Thời gian</p>
                                            <p className="mt-1 font-semibold text-gray-800">{plan.timeline || 'Theo yêu cầu'}</p>
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-500">Bảo hành</p>
                                            <p className="mt-1 font-semibold text-gray-800">{plan.benefits?.warrantyMonths ? `${plan.benefits.warrantyMonths} tháng` : 'Theo yêu cầu'}</p>
                                        </div>
                                    </div>

                                    <div className="mt-5 flex-1">
                                        <p className="mb-3 text-sm font-bold text-gray-900">Gói gồm</p>
                                        <ul className="space-y-2 text-sm text-gray-600">
                                            {highlights.map((highlight) => (
                                                <li key={highlight} className="flex items-start gap-2">
                                                    <FaCheck className="mt-0.5 shrink-0 text-primary-600" />
                                                    <span>{highlight}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <motion.button
                                        onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                                        whileTap={{ scale: 0.97 }}
                                        className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold transition-colors ${plan.popular ? 'bg-primary-600 text-white hover:bg-primary-700' : 'bg-gray-900 text-white hover:bg-primary-600'}`}
                                    >
                                        {plan.buttonText || 'Liên hệ'} <FaArrowRight className="text-xs" />
                                    </motion.button>

                                    <details className="mt-4 border-t border-gray-200 pt-3 text-sm text-gray-500">
                                        <summary className="cursor-pointer font-medium hover:text-primary-700">Nhận báo giá chi tiết</summary>
                                        <div className="mt-3 grid grid-cols-2 gap-2">
                                            <button type="button" onClick={() => generateDocForPlan(plan)} className="border border-primary-200 px-3 py-2 text-primary-700 hover:bg-primary-50">
                                                <FaFileWord className="mr-1 inline text-primary-600" /> Word
                                            </button>
                                            <button type="button" onClick={() => exportPdfForPlan(plan)} className="border border-gray-200 px-3 py-2 text-gray-700 hover:bg-gray-50">
                                                <FaFilePdf className="mr-1 inline text-red-600" /> PDF
                                            </button>
                                        </div>
                                    </details>
                                </div>
                            </motion.div>
                        );
                    };

                    return (
                        <>
                            <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-3 mb-12">
                                {firstRow.map(plan => Card(plan))}
                            </div>
                            <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-2 max-w-4xl mx-auto">
                                {secondRow.map(plan => Card(plan))}
                            </div>
                        </>
                    );
                })()}

            </div>

            {showCompare && (
                <ComparisonModal pricingPlans={pricingPlans} onClose={() => setShowCompare(false)} />
            )}
        </section>
    );
};

// Full-screen comparison modal component
const ComparisonModal = ({ pricingPlans, onClose }) => {
    const { language, t } = useLanguage();
    const featureCatalog = getLocalizedFeatureCatalog(language);
    const [mounted, setMounted] = useState(false);
    React.useEffect(() => {
        setMounted(true);
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [onClose]);

    // Aggregated unique integrations list (order of first appearance)
    const allIntegrations = [];
    pricingPlans.forEach(plan => {
        plan.integrations?.forEach(feat => {
            if (!allIntegrations.includes(feat)) allIntegrations.push(feat);
        });
    });

    // Build cumulative ordered list of all features based on first introduction in plans
    const orderedFeatures = [];
    pricingPlans.forEach(pl => {
        pl.featureAdditions?.forEach(key => {
            if (!orderedFeatures.includes(key)) orderedFeatures.push(key);
        });
    });

    // Benefits keys & labels mapping for table rows
    const benefitRows = [
        { key: 'banners', label: 'Số banner tặng', format: v => v ?? '—' },
        { key: 'dataEntryItems', label: 'Nhập liệu miễn phí (bài / SP)', format: v => v ?? '—' },
        { key: 'ssl', label: 'SSL', format: v => v ? 'Có' : 'Không' },
        { key: 'freeDomain', label: 'Domain miễn phí', format: v => v ? 'Có' : 'Không' },
        { key: 'sourceCode', label: 'Bàn giao mã nguồn', format: v => v ? 'Có' : 'Không' },
        { key: 'warrantyMonths', label: 'Bảo hành (tháng)', format: v => v ?? '—' }
    ];

    const formatPrice = (v) => v === null ? 'Liên hệ' : v.toLocaleString() + '₫';

    const computeAnnual = (plan) => {
        const d = plan.maintenance?.domain;
        const h = plan.maintenance?.hosting;
        const s = plan.maintenance?.support;
        if (!d || !h || !s) return '—';
        const annualDomain = d.annualFee || 0;
        const annualHosting = h.annualFee || 0;
        const annualSupport = s.afterAnnualFee || 0;
        const total = [annualDomain, annualHosting, annualSupport].filter(v => v > 0).reduce((a, b) => a + b, 0);
        return total > 0 ? total.toLocaleString() + '₫/năm' : 'Theo yêu cầu';
    };

    return (
        <div className="fixed inset-0 z-[80]">
            <div
                className="absolute inset-0 bg-gray-900/70 backdrop-blur-sm"
                onClick={onClose}
                aria-hidden="true"
            />
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 40 }}
                transition={{ duration: 0.35 }}
                className="absolute inset-0 flex"
            >
                <div className="relative w-full max-w-7xl mx-auto bg-white shadow-2xl rounded-xl my-6 overflow-hidden flex flex-col">
                    <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-primary-600 to-primary-500">
                        <h3 className="text-lg md:text-xl font-semibold text-white">{t('compare')}</h3>
                        <button
                            onClick={onClose}
                            aria-label="Đóng"
                            className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                        >
                            <FaTimes />
                        </button>
                    </div>
                    <div className="p-4 overflow-auto text-sm leading-relaxed">
                        <div className="min-w-[900px]">
                            <table className="w-full border-separate border-spacing-0">
                                <thead>
                                    <tr>
                                        <th className="sticky top-0 z-10 bg-white/95 backdrop-blur px-4 py-3 text-left text-xs font-semibold text-gray-500 border-b">{language === 'vi' ? 'Tiêu chí' : 'Criteria'}</th>
                                        {pricingPlans.map((p, idx) => (
                                            <th key={p.id} className="sticky top-0 z-10 bg-white/95 backdrop-blur px-4 py-3 text-sm font-semibold text-gray-800 border-b">
                                                <div className="flex flex-col items-start gap-0.5">
                                                    <span>{p.name}</span>
                                                    <span className="text-xs font-normal text-primary-600">{formatPrice(p.price)}</span>
                                                    <span className="text-[10px] font-normal text-gray-400 tracking-wide">
                                                        {idx === 0 ? (language === 'vi' ? 'Tính năng cơ bản' : 'Base features') : `+ ${pricingPlans[idx - 1].name}`}
                                                    </span>
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Basic info */}
                                    <tr>
                                        <td colSpan={pricingPlans.length + 1} className="bg-primary-50 text-primary-700 font-semibold px-4 py-2 text-xs uppercase tracking-wide">{language === 'vi' ? 'Thông tin chung' : 'General information'}</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="px-4 py-2 font-medium text-gray-700">{language === 'vi' ? 'Mô tả' : 'Description'}</td>
                                        {pricingPlans.map(p => <td key={p.id} className="px-4 py-2 text-gray-600">{p.description}</td>)}
                                    </tr>
                                    <tr className="border-b">
                                        <td className="px-4 py-2 font-medium text-gray-700">{t('suitableFor')}</td>
                                        {pricingPlans.map(p => <td key={p.id} className="px-4 py-2 text-gray-600">{p.audience}</td>)}
                                    </tr>
                                    <tr className="border-b">
                                        <td className="px-4 py-2 font-medium text-gray-700">{language === 'vi' ? 'Template / Thiết kế' : 'Template / Design'}</td>
                                        {pricingPlans.map(p => <td key={p.id} className="px-4 py-2 text-gray-600">{p.template}</td>)}
                                    </tr>
                                    <tr className="border-b">
                                        <td className="px-4 py-2 font-medium text-gray-700">{language === 'vi' ? 'Ngôn ngữ' : 'Languages'}</td>
                                        {pricingPlans.map(p => <td key={p.id} className="px-4 py-2 text-gray-600">{p.languages}</td>)}
                                    </tr>
                                    <tr className="border-b">
                                        <td className="px-4 py-2 font-medium text-gray-700">{t('timeline')}</td>
                                        {pricingPlans.map(p => <td key={p.id} className="px-4 py-2 text-gray-600">{p.timeline}</td>)}
                                    </tr>

                                    {/* Integrations */}
                                    <tr>
                                        <td colSpan={pricingPlans.length + 1} className="bg-primary-50 text-primary-700 font-semibold px-4 py-2 text-xs uppercase tracking-wide">{language === 'vi' ? 'Chức năng & tích hợp' : 'Features and integrations'}</td>
                                    </tr>
                                    {allIntegrations.map((feat) => (
                                        <tr key={feat} className="border-b last:border-b-0">
                                            <td className="px-4 py-2 text-gray-700 font-medium">{feat}</td>
                                            {pricingPlans.map(p => (
                                                <td key={p.id + feat} className="px-4 py-2 text-center">
                                                    {p.integrations?.includes(feat) ? <FaCheck className="mx-auto text-primary-600" /> : <span className="text-xs text-gray-300">—</span>}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}

                                    {/* Tier Features cumulative */}
                                    <tr>
                                        <td colSpan={pricingPlans.length + 1} className="bg-primary-50 text-primary-700 font-semibold px-4 py-2 text-xs uppercase tracking-wide">{t('features')}</td>
                                    </tr>
                                    {orderedFeatures.map(fk => {
                                        const feature = featureCatalog.find(fc => fc.key === fk);
                                        return (
                                            <tr key={fk} className="border-b last:border-b-0">
                                                <td className="px-4 py-2 text-gray-700 font-medium">{feature ? feature.label : fk}</td>
                                                {pricingPlans.map((pl, idx) => {
                                                    // Determine if feature is available (introduced in an earlier or same tier)
                                                    const introIndex = pricingPlans.findIndex(p => p.featureAdditions?.includes(fk));
                                                    const included = introIndex !== -1 && idx >= introIndex;
                                                    return (
                                                        <td key={pl.id + fk} className="px-4 py-2 text-center">
                                                            {included ? <FaCheck className="mx-auto text-primary-600" /> : <span className="text-xs text-gray-300">—</span>}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}

                                    {/* Benefits */}
                                    <tr>
                                        <td colSpan={pricingPlans.length + 1} className="bg-primary-50 text-primary-700 font-semibold px-4 py-2 text-xs uppercase tracking-wide">{language === 'vi' ? 'Quyền lợi cụ thể' : 'Benefits'}</td>
                                    </tr>
                                    {benefitRows.map(row => (
                                        <tr key={row.key} className="border-b last:border-b-0">
                                            <td className="px-4 py-2 font-medium text-gray-700">{row.label}</td>
                                            {pricingPlans.map(p => (
                                                <td key={p.id + row.key} className="px-4 py-2 text-center text-gray-700">
                                                    {row.format(p.benefits?.[row.key])}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}

                                    {/* Maintenance */}
                                    <tr>
                                        <td colSpan={pricingPlans.length + 1} className="bg-primary-50 text-primary-700 font-semibold px-4 py-2 text-xs uppercase tracking-wide">{language === 'vi' ? 'Duy trì & bảo hành' : 'Maintenance and warranty'}</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="px-4 py-2 font-medium text-gray-700">Domain (năm miễn phí)</td>
                                        {pricingPlans.map(p => <td key={p.id + "domain"} className="px-4 py-2 text-gray-600 text-center">{p.maintenance?.domain?.includedYears ?? '—'}</td>)}
                                    </tr>
                                    <tr className="border-b">
                                        <td className="px-4 py-2 font-medium text-gray-700">Hosting (năm miễn phí)</td>
                                        {pricingPlans.map(p => <td key={p.id + "hosting"} className="px-4 py-2 text-gray-600 text-center">{p.maintenance?.hosting?.includedYears ?? '—'}</td>)}
                                    </tr>
                                    <tr className="border-b">
                                        <td className="px-4 py-2 font-medium text-gray-700">Support (tháng bảo hành)</td>
                                        {pricingPlans.map(p => <td key={p.id + "support"} className="px-4 py-2 text-gray-600 text-center">{p.maintenance?.support?.includedMonths ?? '—'}</td>)}
                                    </tr>
                                    <tr className="border-b">
                                        <td className="px-4 py-2 font-medium text-gray-700">Chi phí năm sau ưu đãi</td>
                                        {pricingPlans.map(p => <td key={p.id + "annual"} className="px-4 py-2 text-primary-600 text-center font-semibold">{computeAnnual(p)}</td>)}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <button
                                onClick={onClose}
                                className="px-5 py-3 rounded-lg bg-gray-900 hover:bg-primary-600 text-white text-sm font-semibold shadow"
                            >{t('close')}</button>
                            <button
                                onClick={() => { onClose(); const el = document.getElementById('contact'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
                                className="px-5 py-3 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold shadow inline-flex items-center gap-2"
                            >{language === 'vi' ? 'Chọn gói & liên hệ' : 'Choose a plan and contact us'} <FaArrowRight className="text-xs" /></button>
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default PricingPackages;
